/**
 * @deprecated DO NOT USE IN NEW CODE. This mixin is deprecated and will be removed.
 *
 * Use `useSubscription` (`@Composables/useSubscription`) instead.
 *
 * If the behaviour you need is missing from the composable, add it there rather
 * than reaching for this mixin — every new consumer makes the mixin harder to
 * retire. Store access it does not cover (`isUserSubscribed`, `billingPeriod`,
 * plan lists) belongs in the relevant store, not here.
 *
 * This mixin is used by most of the subscription related screens to select subscriptions
 * and send subscription plan changes to the server. This contains helpers for
 * the active subscription, trial information and other convenience helpers.
 */
import { mapActions, mapGetters } from "vuex";

import { uniq, compact, isUndefined, capitalize, groupBy, forEach, isNil } from "lodash-es";
import URI from "urijs";

import { isNullish } from "@tinyurldev/tu-ui-vue";

import BILLING_TYPE from "@Constants/billing-type";
import PADDLE_TRANSACTION_TYPE from "@Constants/paddle-transaction-type";
import SUBSCRIPTION_TYPE from "@Constants/subscription-type";

import MixinBfcm from "@Mixins/bfcm2023";
import MixinBillable from "@Mixins/billable";
import MixinTeams from "@Mixins/teams";

import getStripePaymentLink from "@Helpers/api/getStripePaymentLink";
import { sleep } from "@Helpers/support";
import BillableSubscription from "@Helpers/subscription";

import { cancelSubscriptionApi } from "@Resources/js/api/subscription";

import useNotify from "@Composables/useNotify";
import usePaddle from "@Composables/usePaddle";
import usePaymentMethod from "@Composables/usePaymentMethod";

export default {
  mixins: [MixinBfcm, MixinBillable, MixinTeams],

  data: () => ({
    cancellingSubscription: false,
    selectingSubscription: null,
    isResumeDowngradedLoading: false,
  }),

  /**
   * Captures the composables this mixin forwards to.
   *
   * @internal Use the forwarding methods, not this bag. The prefix keeps it from
   * colliding with the sibling mixins' bags on the shared instance.
   *
   * @remarks Resolved here rather than in `setup()` because Vue has no merge
   * strategy for `setup`, so a component declaring its own would discard the
   * mixin's. Lifecycle hooks merge as arrays, and `created` runs with a current
   * instance, which these composables require.
   */
  created() {
    this.subscriptionComposables = {
      notify: useNotify(),
      paddle: usePaddle(),
      paymentMethod: usePaymentMethod(),
    };
  },

  methods: {
    ...mapActions([
      "refreshState",
      "prepareUserSubscriptions",
      "prepareTeamSubscriptions",
      "prepareOrganizationSubscriptions",
    ]),

    notifyError(options) {
      return this.subscriptionComposables.notify.notifyError(options);
    },

    notifySuccess(options) {
      return this.subscriptionComposables.notify.notifySuccess(options);
    },

    openPaddleCheckout(transactionType, content) {
      return this.subscriptionComposables.paddle.openPaddleCheckout(transactionType, content);
    },

    updatePaymentMethod(subscription) {
      return this.subscriptionComposables.paymentMethod.updatePaymentMethod(subscription);
    },

    /**
     * Cancel the current subscription.
     *
     * Used when cancelling the subscription or downgrading to free.
     */
    cancelSubscription(subscription, url) {
      return new Promise(async (resolve, reject) => {
        const delay = 3000;

        try {
          this.selectingSubscription = subscription;
          this.cancellingSubscription = true;

          const response = await cancelSubscriptionApi(this.getUserBillingType, url);

          await sleep(delay);
          if (subscription.type !== "user") Bus.$emit("updateTeam");
          await this.refreshState();

          resolve(response);
        } catch (error) {
          reject(error);
        } finally {
          this.selectingSubscription = null;
          this.cancellingSubscription = false;
        }
      });
    },

    reactivateSubscription(subscription) {
      this.selectingSubscription = subscription;

      return axios
        .patch(this.urlToReactivateSubscription)
        .then((response) => {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              if (subscription.type !== "user") Bus.$emit("updateTeam");
              this.refreshState();
              resolve(response);
              this.notifySuccess({
                text: this.$t("subscriptions.reactivate_success"),
              });
            }, 3000);
          });
        })
        .catch((error) => {
          this.notifyError({
            text: this.$t("subscriptions.reactivate_failed"),
          });
          return Promise.reject(error);
        })
        .finally(() => {
          this.selectingSubscription = null;
        });
    },

    /**
     * Update the subscription to the given subscription.
     *
     * Used when updating or resuming the subscription.
     */
    updateSubscription(subscription, coupon = null) {
      this.selectingSubscription = subscription;

      const url = this.urlForSubscriptionUpdate;
      const data = {
        billing_type: this.getUserBillingType,
        subscription: subscription.name,
        interval: subscription.interval,
        currency: subscription.price.currency,
        type: subscription.type,
      };
      const config = { DISABLE_ERROR_INTERCEPTORS: true };

      if (!isNullish(coupon)) {
        data.coupon = coupon;
      }

      return axios
        .put(url, data, config)
        .then((response) => response)
        .catch((error) => {
          const { response } = error;

          switch (response && response.status) {
            case 402:
              if (this.getUserBillingType === BILLING_TYPE.STRIPE) {
                window.location = getStripePaymentLink(
                  response.data.paymentId,
                  this.urlForPlanRedirect,
                );
              } else {
                this.notifyError(this.$t("subscriptions.payment_declined"));
                this.updatePaymentMethod(this.getUserCurrentSubscription);
              }
              break;

            case 406:
              this.notifyError({ text: response.data.message });
              break;

            case 422:
              forEach(response.data.errors, (errors) => {
                forEach(errors, (error) => {
                  this.notifyError({ text: error });
                });
              });
              break;

            default:
              this.notifyError({
                text: this.$t("subscriptions.update_failed"),
              });
          }

          return Promise.reject(error);
        })
        .finally(() => {
          this.selectingSubscription = null;
        });
    },

    /**
     * Resumes a subscription.
     */
    resumeSubscription() {
      return new Promise(async (resolve, reject) => {
        const url = this.$apiUrl("settings/subscription/resume");
        const config = { DISABLE_ERROR_INTERCEPTORS: true };
        const delay = 3000;

        try {
          const response = await axios.put(url, config);

          await sleep(delay);
          await this.refreshState();

          resolve(response);
        } catch (error) {
          reject(error);
        } finally {
          this.selectingSubscription = null;
        }
      });
    },

    /**
     * Cancels a schedule to downgrade subscription.
     *
     * @returns The promise that is resolved once the operation is completed.
     */
    cancelDowngradeSchedule() {
      return new Promise(async (resolve, reject) => {
        const url = this.$apiUrl("settings/subscription/scheduled");
        const config = { DISABLE_ERROR_INTERCEPTORS: true };
        const delay = 3000;

        try {
          const response = await axios.delete(url, config);

          await sleep(delay);
          await this.refreshState();

          resolve(response);
        } catch (error) {
          reject(error);
        } finally {
          this.selectingSubscription = null;
        }
      });
    },

    /**
     * Resumes subscription schedule to be downgraded.
     */
    resumeSubscriptionDowngraded() {
      this.isResumeDowngradedLoading = true;

      this.cancelDowngradeSchedule()
        .then(() => {
          this.notifySuccess({
            text: this.$t("subscriptions.resume_success"),
          });
        })
        .catch(() => {
          this.notifyError({
            text: this.$t("subscriptions.resume_failed"),
          });
        })
        .finally(() => {
          this.isResumeDowngradedLoading = false;
        });
    },

    subscribe(subscription, coupon = null, trial = false) {
      this.selectingSubscription = subscription;

      const data = {
        billing_type: this.getUserBillingType,
        subscription: subscription.name,
        interval: subscription.interval,
        currency: subscription.price.currency,
        type: subscription.type,
        trial,
        ...(this.shouldApplyCouponWhenSubscribing(subscription) && {
          coupon: coupon,
        }),
      };

      axios
        .post(this.urlForNewSubscription, data)
        .then(({ data }) => {
          if (data.error) {
            this.notifyError({ text: data.error.message });
          } else {
            // ensure the survey to reappear when a user subscribes or resubscribe
            localStorage.removeItem("isSurveyOnboardingSkipped");

            if (this.getUserBillingType === BILLING_TYPE.PADDLE) {
              this.openPaddleCheckout(PADDLE_TRANSACTION_TYPE.SUBSCRIPTION_PURCHASE, data);
            } else {
              this.openStripeCheckout(data);
            }
          }
        })
        .catch((error) => {
          const { response } = error;

          switch (response && response.status) {
            case 400:
              window.location = getStripePaymentLink(
                response.data.paymentId,
                this.urlForPlanRedirect,
              );
              break;

            case 422:
              forEach(response.data.errors, (error) => {
                forEach(error, (e) => {
                  this.notifyError({ text: e });
                });
              });
              break;

            default:
              this.notifyError({
                text: this.$t("subscriptions.create_failed"),
              });
          }
        })
        .finally(() => {
          this.selectingSubscription = null;
        });
    },

    isSubscriptionFree(subscription) {
      return subscription.price.amount === 0;
    },

    isSelectedSubscription(subscription) {
      return (
        subscription &&
        this.selectingSubscription &&
        this.selectingSubscription.name === subscription.name &&
        this.selectingSubscription.interval === subscription.interval /* &&
        this.selectingSubscription.currency === subscription.price.currency */
      );
    },

    isSubscriptionEnterprise(subscription) {
      return subscription.name == "enterprise";
    },

    isBillableSubscribedTo(subscription) {
      return this.billableSubscriptions.some((billableSubscription) => {
        return (
          billableSubscription.name === subscription.name &&
          billableSubscription.interval === subscription.interval &&
          billableSubscription.currency === subscription.price.currency
        );
      });
    },

    getBillableSubscribedTo(subscription) {
      return this.billableSubscriptions.find((billableSubscription) => {
        return (
          billableSubscription.name === subscription.name &&
          billableSubscription.interval === subscription.interval &&
          billableSubscription.currency === subscription.price.currency
        );
      });
    },

    getPriceByInterval(subscription, interval, currency) {
      const currencies = uniq(
        compact([
          "USD",
          currency.toUpperCase(),
          this.billable.currency?.toUpperCase(),
        ]),
      );

      return subscription.prices.find((price) => {
        return (
          price.active &&
          price.interval === interval &&
          currencies.includes(price.price.currency)
        );
      });
    },

    getSubscriptionInterval(subscription) {
      const interval = this.$t(`billing.interval.${subscription.interval}`);

      return capitalize(interval);
    },

    getSubscriptionName(subscription) {
      return this.$t(`subscriptions:${subscription.name}.title`);
    },

    /**
     * Gets the subscription information.
     *
     * @param {Object} subscription The subscription.
     *
     * @returns The subscription information with the following format [Name]+[Interval].
     */
    getSubscriptionNameWithInterval(subscription) {
      const name = this.getSubscriptionName(subscription);

      if (subscription.name === "free") {
        return name;
      }

      if (!isNil(subscription.interval)) {
        const interval = this.$t(`billing.interval.${subscription.interval}`);

        return `${name} (${capitalize(interval)})`;
      }

      return name;
    },

    getSubscriptionDescription(subscription = undefined) {
      const name = subscription ? subscription.name : "free";

      return this.$t(`subscriptions:${name}.description.long`);
    },

    getSubscriptionTrialDays(subscription = undefined) {
      return this.subscriptions?.find((s) => s.name == subscription?.name)
        ?.trialDays;
    },

    rebuildSubscription(subscription, interval, currency) {
      return new BillableSubscription({
        ...subscription,
        ...this.getPriceByInterval(subscription, interval, currency),
      });
    },

    availableSubscriptions(interval, currency = undefined, showHidden = false) {
      currency = String(currency || this.currentCurrency).toUpperCase();

      return [...this.subscriptions]
        .filter((s) => {
          const isActive = s.active === true;
          const isBillableType = s.type === this.billableType;
          const matchesHidden = !s.hidden || showHidden; // include all when showHidden is true
          const isCustom = s.custom;
          const hasActivePrice = !isUndefined(this.getPriceByInterval(s, interval, currency));

          return (
            isActive
            && isBillableType
            && matchesHidden
            && !isCustom
            && hasActivePrice
          );
        })
        .map((s) => this.rebuildSubscription(s, interval, currency))
        .sort((a, b) => a.price.amount - b.price.amount);
    },

    urlToCancelSubscription(now) {
      let url;

      if (this.billingUser) {
        url = this.getUserSubscriptionUrl;
      } else if (this.billingTeam) {
        url = this.getTeamSubscriptionUrl;
      } else {
        url = this.getOrgSubscriptionUrl;
      }

      // define if subscription will be cancelled immediately
      // no waiting until the end of period
      if (now) {
        url = `${url}/now`;
      }

      return url;
    },

    /**
     * Occurs when the subscription has been reverted the downgrade schedule.
     */
    onRevertSubscription() {
      if (!this.isResumeDowngradedLoading) {
        this.resumeSubscriptionDowngraded();
      }
    },

    openStripeCheckout(data) {
      this.stripe
        .redirectToCheckout({
          sessionId: data.session_id,
        })
        .then((result) => {
          // if stripe fails, bring the flag back
          localStorage.setItem("isSurveyOnboardingSkipped", true);

          this.notifyError({ text: result.error.message });
        });
    },
  },

  computed: {
    ...mapGetters([
      "isUserSubscribed",
      "stripe",
      "userSubscriptions",
      "teamSubscriptions",
      "organizationsSubscriptions",
      "currency",
      "getUserFutureSubscription",
      "getUserBillingType",
      "isFutureSubscriptionScheduled",
    ]),

    billingPeriod: {
      get() {
        return this.$store.state.billingPeriod;
      },
      set(newValue) {
        this.$store.state.billingPeriod = newValue;
      },
    },

    billableSubscriptions() {
      return (
        this.billable?.subscriptions?.current
          ?.filter((d) => d.type === SUBSCRIPTION_TYPE.DEFAULT)
          ?.map((data) => new BillableSubscription(data)) || []
      );
    },

    subscriptions() {
      if (this.billingUser) {
        return this.userSubscriptions;
      } else if (this.billingTeam) {
        return this.teamSubscriptions(this.billable.slug);
      } else {
        return this.organizationsSubscriptions(this.billable.slug);
      }
    },

    groupedSubscriptions() {
      return groupBy(this.subscriptions, (subscription) => {
        return subscription.group || subscription.name;
      });
    },

    subscriptionsLoaded() {
      return this.subscriptions?.length > 0;
    },

    currentCurrency() {
      return (this.billable?.currency || this.currency || "USD").toUpperCase();
    },

    subscriptionsForActiveInterval() {
      return this.availableSubscriptions(
        this.billingPeriod,
        this.currentCurrency,
      );
    },

    subscriptionsForActiveIntervalWithHidden() {
      return this.availableSubscriptions(
        this.billingPeriod,
        this.currentCurrency,
        true,
      );
    },

    /**
     * Determine if the billable entity is on a generic trial.
     */
    onGenericTrial() {
      return (
        this.billable.trial_ends_at &&
        moment.utc(this.billable.trial_ends_at).isAfter(moment.utc())
      );
    },

    /**
     * Get the URL for subscribing to a plan.
     */
    urlForNewSubscription() {
      if (this.billingUser) {
        return this.getUserSubscriptionUrl;
      } else if (this.billingTeam) {
        return this.getTeamSubscriptionUrl;
      } else {
        return this.getOrgSubscriptionUrl;
      }
    },

    /**
     * Get the URL for the subscription plan update.
     */
    urlForSubscriptionUpdate() {
      if (this.billingUser) {
        return this.getUserSubscriptionUrl;
      } else if (this.billingTeam) {
        return this.getTeamSubscriptionUrl;
      } else {
        return this.getOrgSubscriptionUrl;
      }
    },

    /**
     * Get the URL to redirect to after confirmation.
     */
    urlForPlanRedirect() {
      let uri = null;

      if (this.billingUser) {
        uri = this.$appUrl("settings/subscription");
      } else if (this.billingTeam) {
        uri = this.$appUrl(
          "organization/{organization}/team/{team}/profile/subscription",
          {
            organization: this.organization.slug,
            team: this.organizationTeam.slug,
          },
        );
      } else if (this.billingOrganization) {
        uri = this.$appUrl("organization/{organization}/profile/subscription", {
          organization: this.organization.slug,
        });
      }

      return URI(uri).absoluteTo(window.location).toString();
    },

    urlToReactivateSubscription() {
      if (this.billingUser) {
        return this.getUserSubscriptionUrl;
      } else if (this.billingTeam) {
        return this.getTeamSubscriptionUrl;
      } else {
        return this.getOrgSubscriptionUrl;
      }
    },

    isPastDueTrial() {
      const trialEndsAt = this.getUserCurrentSubscription?.trial_ends_at;
      const periodStartsAt = this.getUserCurrentSubscription?.period_starts_at;

      return (
        this.getUserCurrentSubscription?.isPastDue &&
        trialEndsAt === periodStartsAt
      );
    },

    getUserSubscriptionUrl() {
      const url = "settings/subscription";

      return this.$apiUrl(url);
    },

    getTeamSubscriptionUrl() {
      const url = "organization/{organization}/team/{team}/subscription";

      return this.$apiUrl(url, {
        organization: this.organization.slug,
        team: this.organizationTeam.slug,
      });
    },

    getOrgSubscriptionUrl() {
      const url = "organization/{organization}/subscription";

      return this.$apiUrl(url, {
        organization: this.organization.slug,
      });
    },
  },
};
